import { useState } from 'react'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useDispatch } from 'react-redux'

import Styles from './WorkspaceProfileForm.module.scss'
import client from 'api/client'
import * as Actions from 'redux/actions'
import { getInitials } from 'utils'
import { Workspace } from 'types'
import Avatar from 'common/Avatar/Avatar'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ImageUploadField } from './ImageUploadField'
import { useDefaultTheme } from 'hooks/useDefaultTheme'
import { useWorkspaceColorScheme } from 'hooks/useWorkspaceColorScheme'

interface Props {
  workspace: Workspace
  workspaces: Workspace[]
}

export const WorkspaceProfileForm = ({ workspace, workspaces }: Props) => {
  const dispatch = useDispatch()
  const defaultTheme = useDefaultTheme()
  const { colorScheme } = useWorkspaceColorScheme()
  const [workspaceName, setWorkspaceName] = useState(workspace.name)
  const [workspaceProfilePhotoUrl, setWorkspaceProfilePhotoUrl] = useState(workspace.logo_url)

  const handleOnChange = (url: string) => {
    setWorkspaceProfilePhotoUrl(url)
    const newUrl = url
    const update = async () => {
      const res = await client.updateWorkspaceSettings(
        {
          workspace: {
            logo_url: newUrl,
          },
        },
        workspace.key,
      )

      if (res.type === 'success') {
        showToastMessage('Workspace image has been updated!', 'success')

        dispatch(
          Actions.workspacesSet(
            workspaces.map((w) => (w.key === workspace.key ? { ...w, logo_url: newUrl || '' } : w)),
          ),
        )
      } else {
        showToastMessage("Couldn't update your workspace settings, please try again later", 'error')
      }
    }
    update()
  }

  const handleWorkspaceNameSave = async () => {
    if (workspaceName === workspace?.name) return

    const res = await client.updateWorkspaceSettings(
      {
        workspace: {
          name: workspaceName.trim(),
        },
      },
      workspace.key,
    )

    if (res.type === 'success') {
      showToastMessage('Your workspace has been updated!', 'success')
      dispatch(
        Actions.workspacesSet(workspaces.map((w) => (w.key === workspace.key ? { ...w, name: workspaceName } : w))),
      )
    } else {
      showToastMessage("Couldn't update your workspace settings, please try again later", 'error')
    }
  }

  const WorkspaceProfilePhoto = () => (
    <Tooltip label="Add a workspace Avatar" placement="top">
      <div className={Styles.avatar} data-test-id="logo-avatar" data-color-scheme={colorScheme || defaultTheme}>
        <Avatar
          label={getInitials(workspace.name)}
          size={60}
          workspaceId={workspace.id}
          url={workspaceProfilePhotoUrl}
          backgroundColor="var(--avatar)"
        />
      </div>
    </Tooltip>
  )

  return (
    <div className={Styles.profileForm}>
      <div className={Styles.formRow}>
        <div>
          <ImageUploadField
            label="Workspace Avatar"
            cropShape="round"
            onChange={handleOnChange}
            onError={() => showToastMessage("We couldn't process your photo crop, please try again", 'error')}
            value={workspaceProfilePhotoUrl}
            useControls={false}
          >
            <WorkspaceProfilePhoto />
          </ImageUploadField>
        </div>
        <input
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          onBlur={handleWorkspaceNameSave}
          className={Styles.nameInput}
          type="text"
        />
      </div>
    </div>
  )
}
